import React, { useEffect, useState } from "react";
import {
  getAllBanners,
  createBanner,
  updateBanner,
  deleteBanner,
  BANNER_PLACEMENTS
} from "../utils/banners";
import { uploadToCloudinary } from "../utils/cloudinary";
import { formatDate } from "../utils/format";
import {
  Plus,
  Trash2,
  ToggleLeft,
  ToggleRight,
  Upload,
  Image as ImageIcon,
  Sparkles,
  MousePointerClick
} from "lucide-react";
import toast from "react-hot-toast";

export default function AdminBannersPage() {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");

  const [form, setForm] = useState({
    title: "",
    subtitle: "",
    imageUrl: "",
    linkUrl: "/shop",
    placement: "home_mid",
    badge: "Special Offer",
    ctaText: "Shop Now",
    active: true,
  });

  async function load() {
    setLoading(false);
    try {
      const list = await getAllBanners();
      setBanners(list);
    } catch (err) {
      console.error("Error loading banners:", err);
      toast.error("Could not load banners");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const setFormField = (k) => (e) => {
    const val = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm(f => ({ ...f, [k]: val }));
    if (k === "imageUrl") {
      setPreviewUrl(e.target.value);
    }
  };

  // Image file upload
  async function handleImageFileChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      return toast.error("Please select a valid image file (PNG, JPG, WebP)");
    }

    // Local instant preview
    const localUrl = URL.createObjectURL(file);
    setPreviewUrl(localUrl);

    setUploadingImage(true);
    try {
      const res = await uploadToCloudinary(file, "afriport/banners");
      if (res?.url) {
        setForm(f => ({ ...f, imageUrl: res.url }));
        setPreviewUrl(res.url);
        toast.success("Banner image uploaded successfully! 🖼️");
      }
    } catch (err) {
      console.error("Cloudinary upload failed:", err);
      toast.error("Cloud upload error. You can also paste an image URL below.");
    } finally {
      setUploadingImage(false);
    }
  }

  // Create banner
  async function handleCreateBanner(e) {
    e.preventDefault();
    if (!form.imageUrl) {
      return toast.error("Please upload an image or provide an Image URL for the banner");
    }

    try {
      await createBanner({
        title: form.title.trim(),
        subtitle: form.subtitle.trim(),
        imageUrl: form.imageUrl.trim(),
        linkUrl: form.linkUrl.trim(),
        placement: form.placement,
        badge: form.badge.trim(),
        ctaText: form.ctaText.trim(),
        active: Boolean(form.active),
      });

      toast.success("Banner published successfully! 🎉");
      setShowForm(false);
      setPreviewUrl("");
      setForm({
        title: "",
        subtitle: "",
        imageUrl: "",
        linkUrl: "/shop",
        placement: "home_mid",
        badge: "Special Offer",
        ctaText: "Shop Now",
        active: true,
      });
      load();
    } catch (err) {
      console.error("Error creating banner:", err);
      toast.error("Failed to save banner: " + err.message);
    }
  }

  // 1-Click Toggle Active
  async function handleToggleActive(id, currentStatus, title) {
    const newStatus = !currentStatus;
    // Optimistic UI update
    setBanners(prev => prev.map(b => b.id === id ? { ...b, active: newStatus } : b));

    try {
      await updateBanner(id, { active: newStatus });
      toast.success(`Banner is now ${newStatus ? "ACTIVE" : "INACTIVE"}`);
    } catch (err) {
      toast.error("Could not update banner status");
      load();
    }
  }

  // Delete banner
  async function handleDelete(id, title) {
    if (!window.confirm(`Delete this banner?`)) return;
    try {
      await deleteBanner(id);
      toast.success("Banner deleted");
      setBanners(prev => prev.filter(b => b.id !== id));
    } catch (err) {
      toast.error("Failed to delete banner: " + err.message);
    }
  }

  const activeCount = banners.filter(b => b.active).length;
  const totalClicks = banners.reduce((sum, b) => sum + (b.clicks || 0), 0);

  return (
    <div style={{ padding: "clamp(20px, 4vw, 44px)", maxWidth: 1280, margin: "0 auto" }}>
      
      {/* Top Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 28, flexWrap: "wrap", gap: 16 }}>
        <div>
          <h1 style={{ fontFamily: "var(--font-head)", fontSize: 28, fontWeight: 800, color: "var(--text)" }}>
            Ad Banners Management
          </h1>
          <p style={{ color: "var(--text-muted)", fontSize: 15, marginTop: 4 }}>
            Upload slim promotional banners and strategically position them across key pages of your store.
          </p>
        </div>

        <button
          onClick={() => setShowForm(s => !s)}
          className="btn btn-primary"
          style={{ padding: "12px 22px", fontSize: 14, display: "flex", alignItems: "center", gap: 8 }}
        >
          {showForm ? "Cancel" : <><Plus size={18} /> Upload New Banner</>}
        </button>
      </div>

      {/* Metrics Row */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 18, marginBottom: 28 }}>
        <div className="card" style={{ padding: 22, display: "flex", alignItems: "center", gap: 16 }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: "rgba(16, 185, 129, 0.12)", color: "#10B981", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Sparkles size={22} />
          </div>
          <div>
            <p style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 700, textTransform: "uppercase" }}>Active Banners</p>
            <h3 style={{ fontSize: 24, fontWeight: 800, fontFamily: "var(--font-head)", marginTop: 2 }}>
              {activeCount} <span style={{ fontSize: 13, color: "var(--text-muted)", fontWeight: 500 }}>of {banners.length}</span>
            </h3>
          </div>
        </div>

        <div className="card" style={{ padding: 22, display: "flex", alignItems: "center", gap: 16 }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: "rgba(59, 130, 246, 0.12)", color: "#2563EB", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <ImageIcon size={22} />
          </div>
          <div>
            <p style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 700, textTransform: "uppercase" }}>Total Banners</p>
            <h3 style={{ fontSize: 24, fontWeight: 800, fontFamily: "var(--font-head)", marginTop: 2 }}>
              {banners.length}
            </h3>
          </div>
        </div>

        <div className="card" style={{ padding: 22, display: "flex", alignItems: "center", gap: 16 }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: "rgba(168, 85, 247, 0.12)", color: "#9333EA", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <MousePointerClick size={22} />
          </div>
          <div>
            <p style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 700, textTransform: "uppercase" }}>Total Ad Clicks</p>
            <h3 style={{ fontSize: 24, fontWeight: 800, fontFamily: "var(--font-head)", marginTop: 2 }}>
              {totalClicks}
            </h3>
          </div>
        </div>
      </div>

      {/* Upload Banner Form Drawer */}
      {showForm && (
        <div className="card" style={{ padding: 32, marginBottom: 32, border: "1.5px solid var(--brand)", background: "#fff", boxShadow: "0 10px 25px -5px rgba(0,0,0,0.08)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
            <div>
              <h3 style={{ fontFamily: "var(--font-head)", fontWeight: 800, fontSize: 20 }}>Upload & Configure Slim Banner</h3>
              <p style={{ fontSize: 14, color: "var(--text-muted)", marginTop: 2 }}>
                Upload high-resolution slim graphic images (recommended aspect ratio: 8:1 to 12:1, e.g. 1200x120px).
              </p>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 8, background: form.active ? "#F0FDF4" : "#F8FAFC", padding: "6px 14px", borderRadius: 20, border: `1px solid ${form.active ? "#86EFAC" : "var(--border)"}` }}>
              <input
                type="checkbox"
                id="bannerActive"
                checked={form.active}
                onChange={setFormField("active")}
                style={{ cursor: "pointer", width: 16, height: 16 }}
              />
              <label htmlFor="bannerActive" style={{ fontSize: 13, fontWeight: 700, color: form.active ? "#047857" : "var(--text-muted)", cursor: "pointer" }}>
                {form.active ? "Active Immediately" : "Save as Draft / Inactive"}
              </label>
            </div>
          </div>

          <form onSubmit={handleCreateBanner} style={{ display: "flex", flexDirection: "column", gap: 24 }}>
            
            {/* Image Upload Area */}
            <div>
              <label style={{ fontSize: 13, fontWeight: 700, color: "var(--text)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8, display: "block" }}>
                Banner Image *
              </label>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 20, alignItems: "center" }}>
                {/* Upload Button Box */}
                <div
                  style={{
                    border: "2px dashed var(--border)",
                    borderRadius: 12,
                    padding: "24px 20px",
                    textAlign: "center",
                    background: "var(--surface)",
                    position: "relative",
                    cursor: "pointer",
                    transition: "border-color 0.2s",
                  }}
                  onDragOver={e => e.preventDefault()}
                >
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageFileChange}
                    style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", opacity: 0, cursor: "pointer" }}
                  />
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
                    <div style={{ width: 44, height: 44, borderRadius: "50%", background: "var(--brand-light)", color: "var(--brand)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <Upload size={20} />
                    </div>
                    <div>
                      <p style={{ fontWeight: 700, fontSize: 14, color: "var(--text)" }}>
                        {uploadingImage ? "Uploading to Cloudinary…" : "Click or drag image to upload"}
                      </p>
                      <p style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>
                        PNG, JPG, WEBP, SVG (Max 5MB)
                      </p>
                    </div>
                  </div>
                </div>

                {/* Direct URL input option */}
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 600 }}>Or enter direct image URL:</label>
                  <input
                    className="form-input"
                    placeholder="https://example.com/banner-graphic.png"
                    value={form.imageUrl}
                    onChange={setFormField("imageUrl")}
                  />
                </div>
              </div>
            </div>

            {/* Live Banner Preview */}
            {previewUrl && (
              <div>
                <p style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--text-muted)", marginBottom: 8 }}>
                  Live Preview:
                </p>
                <div style={{ borderRadius: 12, overflow: "hidden", border: "1px solid var(--border)", maxHeight: 110, display: "flex", alignItems: "center", justifyContent: "center", background: "#0F172A" }}>
                  <img src={previewUrl} alt="Preview" style={{ width: "100%", maxHeight: 110, objectFit: "cover" }} />
                </div>
              </div>
            )}

            {/* Configuration Fields Grid */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 20 }}>
              
              <div className="form-group">
                <label style={{ fontSize: 12, fontWeight: 700, color: "var(--text)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  Target Placement *
                </label>
                <select className="form-select" value={form.placement} onChange={setFormField("placement")} style={{ fontSize: 14 }}>
                  {BANNER_PLACEMENTS.map(p => (
                    <option key={p.id} value={p.id}>{p.label}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label style={{ fontSize: 12, fontWeight: 700, color: "var(--text)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  Destination Link (Click URL) *
                </label>
                <input
                  className="form-input"
                  placeholder="e.g. /shop?category=Inverters or /solar-calculator"
                  value={form.linkUrl}
                  onChange={setFormField("linkUrl")}
                  required
                />
              </div>

              <div className="form-group">
                <label style={{ fontSize: 12, fontWeight: 700, color: "var(--text)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  Banner Title (Optional / Alt Text)
                </label>
                <input
                  className="form-input"
                  placeholder="e.g. 20% Off Lithium Solar Batteries"
                  value={form.title}
                  onChange={setFormField("title")}
                />
              </div>

              <div className="form-group">
                <label style={{ fontSize: 12, fontWeight: 700, color: "var(--text)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  Subtitle / Promo Code Tag
                </label>
                <input
                  className="form-input"
                  placeholder="e.g. Use code SOLAR20 at checkout"
                  value={form.subtitle}
                  onChange={setFormField("subtitle")}
                />
              </div>

              <div className="form-group">
                <label style={{ fontSize: 12, fontWeight: 700, color: "var(--text)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  Badge Tag (Optional)
                </label>
                <input
                  className="form-input"
                  placeholder="e.g. FLASH SALE, SPONSORED, NEW"
                  value={form.badge}
                  onChange={setFormField("badge")}
                />
              </div>

              <div className="form-group">
                <label style={{ fontSize: 12, fontWeight: 700, color: "var(--text)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  CTA Button Text (Optional)
                </label>
                <input
                  className="form-input"
                  placeholder="e.g. Shop Now or Learn More"
                  value={form.ctaText}
                  onChange={setFormField("ctaText")}
                />
              </div>

            </div>

            {/* Submit & Cancel */}
            <div style={{ display: "flex", gap: 12, borderTop: "1px solid var(--border)", paddingTop: 20 }}>
              <button
                type="submit"
                disabled={uploadingImage}
                className="btn btn-primary"
                style={{ padding: "12px 28px" }}
              >
                {uploadingImage ? "Uploading Image…" : "Save & Publish Banner"}
              </button>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="btn btn-secondary"
                style={{ padding: "12px 20px" }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Banners Table */}
      <div className="card" style={{ padding: 0, overflow: "hidden", border: "1px solid var(--border)", background: "#fff" }}>
        <div style={{ padding: "18px 24px", borderBottom: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3 style={{ fontFamily: "var(--font-head)", fontSize: 18, fontWeight: 700 }}>
            Configured Banners ({banners.length})
          </h3>
        </div>

        {loading ? (
          <div style={{ padding: 60, textAlign: "center" }}><div className="loader"><div className="spinner" /></div></div>
        ) : banners.length === 0 ? (
          <div style={{ textAlign: "center", padding: "60px 20px" }}>
            <ImageIcon size={44} color="var(--text-muted)" style={{ marginBottom: 12 }} />
            <h3 style={{ fontFamily: "var(--font-head)", fontWeight: 700, marginBottom: 6 }}>No banners uploaded yet</h3>
            <p style={{ color: "var(--text-muted)", fontSize: 14, maxWidth: 360, margin: "0 auto 20px" }}>
              Upload your first slim ad banner to display across your storefront pages.
            </p>
            <button onClick={() => setShowForm(true)} className="btn btn-primary btn-sm">
              <Plus size={14} /> Upload Banner
            </button>
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
              <thead>
                <tr style={{ background: "var(--surface)", borderBottom: "2px solid var(--border)" }}>
                  {["Banner Preview", "Placement", "Destination", "Clicks", "Status", "Created", ""].map(h => (
                    <th key={h} style={{ padding: "14px 18px", textAlign: "left", fontWeight: 700, fontSize: 12, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--text-muted)" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {banners.map(b => {
                  const placementLabel = BANNER_PLACEMENTS.find(p => p.id === b.placement)?.label || b.placement;

                  return (
                    <tr
                      key={b.id}
                      style={{
                        borderBottom: "1px solid var(--border)",
                        transition: "background 0.15s",
                        background: !b.active ? "rgba(0,0,0,0.015)" : "transparent",
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = "#F8FAFC"}
                      onMouseLeave={e => e.currentTarget.style.background = !b.active ? "rgba(0,0,0,0.015)" : "transparent"}
                    >
                      {/* Preview */}
                      <td style={{ padding: "14px 18px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                          <div style={{ width: 140, height: 42, borderRadius: 6, overflow: "hidden", background: "#0F172A", border: "1px solid var(--border)", flexShrink: 0 }}>
                            <img src={b.imageUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                          </div>
                          <div>
                            <div style={{ fontWeight: 700, fontSize: 13, color: "var(--text)" }}>
                              {b.title || "Graphic Banner"}
                            </div>
                            {b.badge && (
                              <span style={{ fontSize: 10, fontWeight: 700, color: "var(--brand)", textTransform: "uppercase" }}>
                                {b.badge}
                              </span>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Placement */}
                      <td style={{ padding: "14px 18px" }}>
                        <span
                          className="badge"
                          style={{
                            background: "rgba(30, 58, 138, 0.08)",
                            color: "var(--brand)",
                            fontWeight: 700,
                            fontSize: 12,
                            padding: "4px 8px",
                            borderRadius: 6,
                          }}
                        >
                          {placementLabel}
                        </span>
                      </td>

                      {/* Destination */}
                      <td style={{ padding: "14px 18px", fontSize: 13 }}>
                        <span style={{ fontFamily: "monospace", color: "var(--text-muted)" }}>
                          {b.linkUrl}
                        </span>
                      </td>

                      {/* Clicks */}
                      <td style={{ padding: "14px 18px" }}>
                        <span style={{ fontWeight: 700, fontSize: 14 }}>
                          {b.clicks || 0}
                        </span>
                        <span style={{ fontSize: 12, color: "var(--text-muted)", marginLeft: 4 }}>
                          clicks
                        </span>
                      </td>

                      {/* Active Status & 1-Click Toggle */}
                      <td style={{ padding: "14px 18px" }}>
                        <button
                          onClick={() => handleToggleActive(b.id, b.active, b.title)}
                          style={{
                            border: "1px solid",
                            borderColor: b.active ? "#86EFAC" : "#CBD5E1",
                            background: b.active ? "#F0FDF4" : "#F8FAFC",
                            borderRadius: 20,
                            padding: "6px 12px",
                            cursor: "pointer",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                            color: b.active ? "#047857" : "#64748B",
                            transition: "all 0.2s",
                          }}
                          title={b.active ? "Click to deactivate" : "Click to activate"}
                        >
                          {b.active ? <ToggleRight size={20} color="#10B981" /> : <ToggleLeft size={20} color="#94A3B8" />}
                          <span style={{ fontSize: 12, fontWeight: 700 }}>
                            {b.active ? "Active" : "Inactive"}
                          </span>
                        </button>
                      </td>

                      {/* Created */}
                      <td style={{ padding: "14px 18px", color: "var(--text-muted)", fontSize: 13 }}>
                        {formatDate(b.createdAt)}
                      </td>

                      {/* Delete */}
                      <td style={{ padding: "14px 18px", textAlign: "right" }}>
                        <button
                          onClick={() => handleDelete(b.id, b.title)}
                          style={{
                            border: "none",
                            color: "#EF4444",
                            padding: 8,
                            borderRadius: 6,
                            background: "rgba(239, 68, 68, 0.08)",
                            display: "inline-flex",
                            alignItems: "center",
                            cursor: "pointer",
                            transition: "background 0.2s",
                          }}
                          title="Delete Banner"
                        >
                          <Trash2 size={15} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

    </div>
  );
}
